import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Badge, Button, Checkbox, Input } from '@roger/ui-components';
import { Archive, Loader2, RotateCcw } from 'lucide-react';

import {
  archivePatientTagDefinition,
  createPatientTagDefinition,
  getPatientTagDefinitions,
  reactivatePatientTagDefinition,
  renamePatientTagDefinition,
  type PatientTagDefinitionResponse,
} from '../../services/patientTagsApi';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

interface PatientTagsSectionProps {
  /** The parent Patient Creation card owns the clinic picker. */
  clinicId: string;
  canEdit: boolean;
}

type RequestIdentity = {
  clinicId: string;
  includeInactive: boolean;
};

const requestKey = ({ clinicId, includeInactive }: RequestIdentity) =>
  `${clinicId}:${includeInactive}`;

export const PatientTagsSection: React.FC<PatientTagsSectionProps> = ({
  clinicId,
  canEdit,
}) => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );
  const [definitions, setDefinitions] = useState<
    PatientTagDefinitionResponse[]
  >([]);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadErrorFor, setLoadErrorFor] = useState<string | null>(null);
  const requestGeneration = useRef(0);
  const currentIdentityRef = useRef<RequestIdentity>({
    clinicId,
    includeInactive,
  });
  currentIdentityRef.current = { clinicId, includeInactive };
  const loadedIdentityRef = useRef<string | null>(null);

  const isCurrentIdentity = (identity: RequestIdentity) =>
    currentIdentityRef.current.clinicId === identity.clinicId &&
    currentIdentityRef.current.includeInactive === identity.includeInactive;

  const load = useCallback(async () => {
    const identity = { clinicId, includeInactive };
    // A mutation handler from an earlier render may call this callback after
    // the parent selector moved. Do not issue a follow-up read for that old
    // clinic, and never let its response repaint the selected clinic.
    if (!isCurrentIdentity(identity)) return;

    const generation = ++requestGeneration.current;
    const key = requestKey(identity);
    loadedIdentityRef.current = null;
    setLoadErrorFor(null);
    if (!clinicId) {
      setDefinitions([]);
      setLoading(false);
      loadedIdentityRef.current = key;
      return;
    }

    setLoading(true);
    try {
      const nextDefinitions = await getPatientTagDefinitions({
        clinicId,
        includeInactive,
      });
      if (
        generation !== requestGeneration.current ||
        !isCurrentIdentity(identity)
      ) {
        return;
      }
      setDefinitions(nextDefinitions);
      loadedIdentityRef.current = key;
    } catch (error) {
      console.error('Failed to load patient tags', error);
      if (
        generation === requestGeneration.current &&
        isCurrentIdentity(identity)
      ) {
        setLoadErrorFor(key);
        showError(
          getBusinessErrorMessage(error) ?? 'Unable to load patient tags.'
        );
      }
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [clinicId, includeInactive, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveNew = async () => {
    const name = newName.trim();
    const identity = { clinicId, includeInactive };
    if (!clinicId || !name) return;
    try {
      await createPatientTagDefinition({ clinic_id: clinicId, name });
      if (!isCurrentIdentity(identity)) return;
      setNewName('');
      await load();
      if (isCurrentIdentity(identity)) showSuccess('Tag added');
    } catch (error) {
      console.error('Failed to create patient tag', error);
      if (isCurrentIdentity(identity)) {
        showError(getBusinessErrorMessage(error) ?? 'Unable to add tag.');
      }
    }
  };

  const rename = async (definition: PatientTagDefinitionResponse) => {
    const name = editingName.trim();
    const identity = { clinicId, includeInactive };
    if (!name) return;
    try {
      await renamePatientTagDefinition(definition.id, { name });
      if (!isCurrentIdentity(identity)) return;
      setEditingId(null);
      await load();
      if (isCurrentIdentity(identity)) showSuccess('Tag renamed');
    } catch (error) {
      console.error('Failed to rename patient tag', error);
      if (isCurrentIdentity(identity)) {
        showError(getBusinessErrorMessage(error) ?? 'Unable to rename tag.');
      }
    }
  };

  const toggleActive = async (definition: PatientTagDefinitionResponse) => {
    const identity = { clinicId, includeInactive };
    try {
      if (definition.is_active) {
        await archivePatientTagDefinition(definition.id);
      } else {
        await reactivatePatientTagDefinition(definition.id);
      }
      if (!isCurrentIdentity(identity)) return;
      await load();
      if (isCurrentIdentity(identity)) {
        showSuccess(definition.is_active ? 'Tag archived' : 'Tag reactivated');
      }
    } catch (error) {
      console.error('Failed to change patient tag status', error);
      if (isCurrentIdentity(identity)) {
        showError(getBusinessErrorMessage(error) ?? 'Unable to update tag.');
      }
    }
  };

  const currentIdentityKey = requestKey({ clinicId, includeInactive });
  const loadError = loadErrorFor === currentIdentityKey;
  const waitingForCurrentData =
    Boolean(clinicId) &&
    !loadError &&
    loadedIdentityRef.current !== currentIdentityKey;

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-lg font-semibold'>Patient Tags</h2>
        <p className='text-sm text-muted-foreground'>
          Create labels for the selected clinic. Archived tags stay on existing
          charts and intakes but cannot be selected for new records.
        </p>
      </div>
      <label className='flex items-center gap-2 text-sm'>
        <Checkbox
          id='patient-tags-show-archived'
          checked={includeInactive}
          onCheckedChange={checked => setIncludeInactive(checked === true)}
        />
        <span>Show archived</span>
      </label>
      {canEdit && (
        <div className='flex flex-col gap-2 sm:flex-row'>
          <Input
            inputSize='sm'
            className='w-full sm:max-w-sm'
            value={newName}
            onChange={event => setNewName(event.target.value)}
            placeholder='New tag name'
            maxLength={80}
            onKeyDown={event => {
              if (event.key === 'Enter') void saveNew();
            }}
          />
          <Button
            size='sm'
            onClick={() => void saveNew()}
            disabled={!newName.trim()}
          >
            Add tag
          </Button>
        </div>
      )}
      {loading || waitingForCurrentData ? (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' /> Loading tags...
        </div>
      ) : loadError ? (
        <div className='space-y-2 rounded-md border p-4' role='alert'>
          <p className='text-sm text-destructive'>
            Unable to load patient tags. Please try again.
          </p>
          <Button size='sm' variant='outline' onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : (
        <div className='divide-y rounded-md border'>
          {definitions.map(definition => (
            <div
              key={definition.id}
              className='flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between'
            >
              {editingId === definition.id ? (
                <Input
                  inputSize='sm'
                  value={editingName}
                  onChange={event => setEditingName(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') void rename(definition);
                    if (event.key === 'Escape') setEditingId(null);
                  }}
                  autoFocus
                />
              ) : (
                <div className='flex items-center gap-2'>
                  <span className='font-medium'>{definition.name}</span>
                  {!definition.is_active && (
                    <Badge variant='secondary'>Archived</Badge>
                  )}
                </div>
              )}
              {canEdit && (
                <div className='flex flex-wrap gap-2'>
                  {editingId === definition.id ? (
                    <Button size='sm' onClick={() => void rename(definition)}>
                      Save
                    </Button>
                  ) : (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => {
                        setEditingId(definition.id);
                        setEditingName(definition.name);
                      }}
                    >
                      Rename
                    </Button>
                  )}
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => void toggleActive(definition)}
                  >
                    {definition.is_active ? (
                      <>
                        <Archive className='mr-1 h-4 w-4' /> Archive
                      </>
                    ) : (
                      <>
                        <RotateCcw className='mr-1 h-4 w-4' /> Reactivate
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ))}
          {definitions.length === 0 && (
            <p className='p-4 text-sm text-muted-foreground'>
              No tags for this clinic.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
